import { Head } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"

interface Props {
  org_slug: string
  onboarding_state: string | null
}

export default function Provisioning({ org_slug, onboarding_state }: Props) {
  const [state, setState] = useState(onboarding_state ?? "provisioning")
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0
    const startedAt = Date.now()
    const MAX_ELAPSED_MS = 90_000

    const nextDelay = () => {
      // 2s for first 5 attempts, then exponential backoff capped at 30s.
      if (attempts <= 5) return 2_000
      const exp = 2_000 * Math.pow(2, attempts - 5)
      return Math.min(exp, 30_000)
    }

    const poll = async () => {
      if (cancelled) return
      if (Date.now() - startedAt > MAX_ELAPSED_MS) {
        setTimedOut(true)
        return
      }
      attempts += 1
      try {
        const res = await fetch(`/provisioning/${org_slug}.json`, {
          headers: { Accept: "application/json" },
        })
        if (cancelled) return
        if (res.ok) {
          const data = await res.json()
          if (data.state) setState(data.state)
          if (data.state === "demo" || data.state === "claimed") {
            window.location.href = `/${org_slug}/`
            return
          }
        }
      } catch {
        // network blip — try again
      }
      timer = setTimeout(poll, nextDelay())
    }

    if (state === "provisioning") poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [org_slug, state])

  if (timedOut) {
    return (
      <AuthLayout
        title="Still working on it"
        description="This is taking longer than usual. Refresh to keep checking, or contact support if it doesn't resolve."
      >
        <Head title="Provisioning" />
      </AuthLayout>
    )
  }

  if (state === "provisioning_failed") {
    return (
      <AuthLayout
        title="We hit a snag"
        description="Warren has been notified — we'll reach out within an hour."
      >
        <Head title="Provisioning failed" />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Setting up your demo"
      description="Pulling sample data into your tenant. This usually takes about 30 seconds."
    >
      <Head title="Provisioning" />
      <div className="flex items-center justify-center gap-3 py-8">
        <Spinner />
        <span className="text-muted-foreground text-sm">{state}</span>
      </div>
    </AuthLayout>
  )
}
